import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar,
  faChevronRight,
  faBrain,
  faTrophy,
  faExclamationTriangle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';

const TestReviewDomains = ({ domains, onDomainClick, theme }) => {
  const styles = {
    container: {
      background: theme?.background?.secondary,
      borderRadius: '1.25rem',
      padding: '1.5rem',
    },
    header: {
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    titleIcon: {
      fontSize: '1.125rem',
      color: theme?.primary?.teal?.[600] || '#0891b2',
    },
    domainList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1rem',
    },
    domainItem: {
      padding: '1.25rem',
      borderRadius: '0.75rem',
      background: theme?.background?.tertiary,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minHeight: '140px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    domainHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '0.75rem',
    },
    domainInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flex: 1,
    },
    domainIcon: {
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.125rem',
    },
    domainName: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      flex: 1,
    },
    domainScore: {
      fontSize: '1.125rem',
      fontWeight: '700',
    },
    domainStats: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      fontSize: '0.75rem',
      color: theme?.text?.secondary,
      marginBottom: '0.5rem',
    },
    progressBar: {
      width: '100%',
      height: '0.5rem',
      background: theme?.backgrounds?.tertiary,
      borderRadius: '0.25rem',
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      borderRadius: '0.25rem',
      transition: 'width 1s ease',
    },
    chevron: {
      fontSize: '1rem',
      color: theme?.text?.tertiary,
    },
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return theme?.primary?.green?.[500];
    if (percentage >= 50) return theme?.primary?.yellow?.[500];
    return theme?.primary?.red?.[500];
  };

  const getScoreBgColor = (percentage) => {
    if (percentage >= 80) return `${theme?.primary?.green?.[500]}15`;
    if (percentage >= 50) return `${theme?.primary?.yellow?.[500]}15`;
    return `${theme?.primary?.red?.[500]}15`;
  };

  const getScoreIcon = (percentage) => {
    if (percentage >= 80) return faTrophy;
    if (percentage >= 50) return faExclamationTriangle;
    return faTimesCircle;
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div style={styles.header}>
        <h2 style={styles.title}>
          <FontAwesomeIcon icon={faChartBar} style={styles.titleIcon} />
          Domain-wise Performance
        </h2>
      </div>

      <div className='grid gap-4' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))' }}>
        {domains.map((domain, index) => {
          const scoreColor = getScoreColor(domain.percentage);
          const scoreBgColor = getScoreBgColor(domain.percentage);
          const scoreIcon = getScoreIcon(domain.percentage);
          
          return (
            <motion.div
              key={domain.id}
              style={styles.domainItem}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{
                y: -5,
                boxShadow: theme?.shadows?.sm,
                scale: 1.02,
              }}
              onClick={() => onDomainClick(domain)}
            >
              <div style={styles.domainHeader}>
                <div style={styles.domainInfo}>
                  <div style={{
                    ...styles.domainIcon,
                    background: scoreBgColor,
                    color: scoreColor,
                  }}>
                    <FontAwesomeIcon icon={scoreIcon} />
                  </div>
                  <div style={styles.domainName}>{domain.title}</div>
                </div>
                <div style={{
                  ...styles.domainScore,
                  color: scoreColor,
                }}>
                  {domain.percentage.toFixed(0)}%
                </div>
                <FontAwesomeIcon icon={faChevronRight} style={styles.chevron} />
              </div>
              
              <div style={styles.domainStats}>
                <span>{domain.correct} correct</span>
                <span>•</span>
                <span>{domain.total} total</span>
              </div>
              
              <div style={styles.progressBar}>
                <motion.div
                  style={{
                    ...styles.progressFill,
                    background: scoreColor,
                    width: `${domain.percentage}%`,
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${domain.percentage}%` }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.5 + index * 0.1 }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default TestReviewDomains;